import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import GenerateInvoiceModal from "src/components/modals/GenerateInvoiceModal";
import { toast } from "react-toastify";
import salesApis from "src/api/salesOrder";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { Loader2, FileDown, ArrowLeft } from "lucide-react";
import { useAppSelector } from "src/redux/hooks";

const SalesOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;

  useEffect(() => {
    if (orderId) getOrderDetails(orderId);
  }, [orderId]);

  const getOrderDetails = async (orderId: string) => {
    try {
      setLoading(true);
      const res = await salesApis.getOrderById(orderId);
      setOrder(res?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-indigo-600" size={28} />
          <span className="ml-3 text-gray-700">Loading order...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-gray-500">
          Order not found or deleted.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      meta={{
        title: `Order ${order.orderId} | Invyntra Dashboard`,
        description: "View full sale order details and generate invoices.",
        keywords: "sales order details, invoice, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800 flex items-center text-sm"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </button>
          </div>

          <Breadcrumb
            data={[
              { label: "Home", path: "/app/dashboard" },
              { label: "Sales", path: "/app/sale/list" },
              { label: `#${order.orderId}`, path: `/app/sale/${orderId}` },
            ]}
          />
        </div>

        <div className="space-y-6 pb-4">
          <h1 className="text-xl font-semibold text-gray-800">
            Order #{order.orderId}
          </h1>

          {/* Customer Info */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Customer Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <p>
                <span className="font-medium">Name: </span>
                {order.customerId?.name}
              </p>
              <p>
                <span className="font-medium">Mobile: </span>
                {order.customerId?.mobile}
              </p>
              <p>
                <span className="font-medium">Email: </span>
                {order.customerId?.email || "-"}
              </p>
              <p>
                <span className="font-medium">Address: </span>
                {order.customerId?.address || "-"}
              </p>
              <p>
                <span className="font-medium">State: </span>
                {order.customerId?.state || "-"}
              </p>
              <p>
                <span className="font-medium">GSTIN: </span>
                {order.customerId?.gstNumber || "-"}
              </p>
            </div>
          </Card>

          {/* Products */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Ordered Products
            </h3>

            <TableV2
              columns={[
                {
                  key: "productId.name",
                  header: "Product",
                  accessor: ({ row }) => row.productId?.name,
                },
                {
                  key: "hsnCode",
                  header: "HSN",
                  accessor: ({ row }) =>
                    row.hsnCode || row.productId?.hsnCode || "-",
                },
                { key: "quantity", header: "Qty", align: "right" },
                {
                  key: "sellingPrice",
                  header: "Price",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.sellingPrice.toFixed(2)),
                },
                {
                  key: "discountPercent",
                  header: "Disc (%)",
                  align: "right",
                },
                {
                  key: "taxPercent",
                  header: "GST %",
                  align: "right",
                  accessor: ({ row }) => `${row.taxPercent ?? 0}%`,
                },
                {
                  key: "taxAmount",
                  header: "Tax",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter((row.taxAmount ?? 0).toFixed(2), "IN", 2),
                },
                {
                  key: "totalPrice",
                  header: "Total",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.totalPrice.toFixed(2), "IN", 2),
                },
              ]}
              data={order.items || []}
            />
          </Card>

          {/* Payment Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Payment Summary
            </h3>

            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <p>
                <span className="font-medium text-slate-500">Subtotal:</span>{" "}
                <span className="font-semibold">
                  {currencyFormatter(order.subTotal)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500">Tax:</span>{" "}
                <span className="font-semibold">
                  {currencyFormatter(order.taxAmount)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500">Discount:</span>{" "}
                <span className="font-semibold">
                  {currencyFormatter(order.discount)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500">Grand Total:</span>{" "}
                <span className="font-semibold">
                  {currencyFormatter(order.grandTotal)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500">
                  Payment Mode:
                </span>{" "}
                <span className="font-semibold">
                  {order.paymentMode?.toUpperCase()}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500">
                  Payment Status:
                </span>{" "}
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    order.paymentStatus === "paid"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {order.paymentStatus}
                </span>
              </p>
            </div>
          </Card>

          {/* Meta Info + Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p>
                <span className="font-medium">Created By:</span>{" "}
                {order.createdBy?.firstName || "Staff"}
              </p>
              <p>
                <span className="font-medium">Created At:</span>{" "}
                {formatDateTime(order.createdAt, "DD/MM/YYYY hh:mm A")}
              </p>
            </div>

            <Button
              onClick={() => setInvoiceModalOpen(true)}
              className="bg-indigo-600 text-white flex items-center"
            >
              <FileDown size={16} className="mr-2" /> Generate Invoice
            </Button>
          </div>
        </div>
      </div>

      <GenerateInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        order={order}
        tenant={tenant}
      />
    </DashboardLayout>
  );
};

export default SalesOrderDetailsPage;
